// Creative item palette (D8): a searchable grid over the whole item registry
// plus the player hotbar/storage. Palette slots are an infinite source — a click
// fills the cursor with a full stack, and dropping a held stack onto the palette
// deletes it (creative trash). The player-inventory half runs through the shared
// cursor state machine (transaction.clickSlot). Client-only (ENG-1).
import { h, clear } from '../h';
import { type ItemStack, emptyStack, isEmpty, makeStack, cloneStack } from '../../../sim/game/inventory';
import { clickSlot, type ClickWindow, type ClickButton } from '../../../sim/game/transaction';
import { maxStackFn } from './crafting';
import type { Game } from '../../boot';

const COLS = 9;

/** Creative search tabs (UI-4). Items are bucketed by a light heuristic. */
const TABS = ['all', 'building', 'decoration', 'tools', 'combat', 'food', 'misc'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = {
  all: 'All', building: 'Build', decoration: 'Decor', tools: 'Tools', combat: 'Combat', food: 'Food', misc: 'Misc',
};

export function openCreative(game: Game): void {
  const reg = game.registries;
  const pinv = game.playerState.inventory;
  const maxStack = maxStackFn(reg);
  const allIds = reg.items.list
    .map((i) => reg.items.idOf.get(i.name)!)
    .filter((id) => id !== 0);

  const categoryOf = (id: number): Tab => {
    const def = reg.items.byName.get(reg.items.nameOf[id] ?? '');
    if (!def) return 'misc';
    if ((def.attackDamage ?? 0) > 1) return 'combat';
    if (def.toolClass || def.tags?.includes('tool')) return 'tools'; // a name tag is a tool (§28.9)
    if (def.tags?.includes('food')) return 'food';
    if (def.block) {
      const b = reg.blocks.byName.get(def.block);
      const shape = b?.shape ?? 'cube';
      if (shape === 'cross' || shape === 'torch' || shape === 'bed' || b?.material === 'plant') return 'decoration';
      return 'building';
    }
    return 'misc';
  };
  const catById = new Map(allIds.map((id) => [id, categoryOf(id)] as const));

  const hotbar = range(0, 9), storage = range(9, 36);
  // The player half reuses the cursor state machine directly over the inventory.
  const playerWin: ClickWindow = {
    slots: pinv,
    quickMoveTargets: (from) => (from < 9 ? storage : from < 36 ? hotbar : [...hotbar, ...storage]),
  };

  let cursor: ItemStack = emptyStack();
  let query = '';
  let tab: Tab = 'all';

  game.uiOpen = true;
  document.exitPointerLock();

  const cursorEl = h('div', { class: 'bb-cursor' });
  const overlay = h('div', { class: 'bb-window-overlay' });
  const panel = h('div', { class: 'bb-window bb-creative' });
  overlay.append(panel, cursorEl);
  document.body.append(overlay);

  const paintCursor = (): void => {
    clear(cursorEl);
    if (!isEmpty(cursor)) {
      cursorEl.append(h('div', { class: 'bb-slot-icon', style: { backgroundImage: `url(${game.icons.get(cursor.item)})` } }));
      if (cursor.count > 1) cursorEl.append(h('span', { class: 'bb-slot-count' }, String(cursor.count)));
    }
  };

  // ── Player hotbar/storage cells (real slots). ──
  const invCells = new Map<number, HTMLElement>();
  const paintInv = (i: number): void => {
    const el = invCells.get(i)!;
    clear(el);
    const s = pinv.get(i);
    if (!isEmpty(s)) {
      el.append(h('div', { class: 'bb-slot-icon', style: { backgroundImage: `url(${game.icons.get(s.item)})` } }));
      if (s.count > 1) el.append(h('span', { class: 'bb-slot-count' }, String(s.count)));
    }
  };
  const invCell = (i: number): HTMLElement => {
    const el = h('div', { class: 'bb-slot', 'data-slot': `inv-${i}` });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const button: ClickButton = e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left';
      cursor = clickSlot(playerWin, cursor, { slot: i, button, shift: e.shiftKey });
      for (const j of [...hotbar, ...storage]) paintInv(j);
      paintCursor();
    });
    invCells.set(i, el);
    return el;
  };

  // ── Palette (creative source): click grabs a full stack, drop deletes. ──
  const paletteWrap = h('div', { class: 'bb-creative-palette' });
  const renderPalette = (): void => {
    clear(paletteWrap);
    const q = query.trim().toLowerCase();
    const ids = allIds.filter((id) =>
      (tab === 'all' || catById.get(id) === tab) && (!q || (reg.items.nameOf[id] ?? '').includes(q)));
    const rows = Math.max(1, Math.ceil(ids.length / COLS));
    for (let r = 0; r < rows; r++) {
      const row = h('div', { class: 'bb-slot-row' });
      for (let c = 0; c < COLS; c++) {
        const id = ids[r * COLS + c];
        if (id === undefined) { row.append(h('div', { class: 'bb-slot bb-slot-gap' })); continue; }
        const name = reg.items.nameOf[id] ?? '';
        const el = h('div', { class: 'bb-slot bb-slot-creative', 'data-slot': `pal-${name}`, title: name },
          h('div', { class: 'bb-slot-icon', style: { backgroundImage: `url(${game.icons.get(id)})` } }));
        el.addEventListener('contextmenu', (e) => e.preventDefault());
        el.addEventListener('mousedown', (e) => {
          e.preventDefault();
          if (isEmpty(cursor)) {
            const cap = maxStack(id);
            cursor = makeStack(id, e.button === 2 ? Math.max(1, Math.ceil(cap / 2)) : cap);
          } else if (cursor.item === id && e.button === 2) {
            cursor = { ...cloneStack(cursor), count: Math.min(maxStack(id), cursor.count + 1) };
          } else {
            cursor = emptyStack(); // drop onto the palette → trash
          }
          paintCursor();
        });
        row.append(el);
      }
      paletteWrap.append(row);
    }
  };

  // ── Assemble ──
  const search = h('input', {
    class: 'bb-input bb-creative-search',
    placeholder: 'Search items…',
    spellcheck: false,
  }) as HTMLInputElement;
  search.addEventListener('input', () => { query = search.value; renderPalette(); });
  search.addEventListener('keydown', (e) => { if (e.code === 'Escape' || (e.code === 'KeyE' && !query)) { e.stopPropagation(); close(); } });

  // Category tabs (UI-4): filter the palette by item bucket.
  const tabBar = h('div', { class: 'bb-creative-tabs' });
  const tabButtons = new Map<Tab, HTMLElement>();
  for (const t of TABS) {
    const btn = h('button', { class: t === tab ? 'bb-tab bb-tab-active' : 'bb-tab', type: 'button', 'data-tab': t }, TAB_LABEL[t]);
    btn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      tab = t;
      for (const [tt, b] of tabButtons) b.classList.toggle('bb-tab-active', tt === t);
      renderPalette();
    });
    tabButtons.set(t, btn);
    tabBar.append(btn);
  }
  panel.append(h('div', { class: 'bb-window-title' }, 'Creative Inventory'), tabBar, search, paletteWrap);
  const invRows = h('div', { class: 'bb-creative-inv' });
  for (let r = 0; r < 3; r++) {
    const row = h('div', { class: 'bb-slot-row' });
    for (const i of storage.slice(r * 9, r * 9 + 9)) row.append(invCell(i));
    invRows.append(row);
  }
  invRows.append(h('div', { class: 'bb-slot-row' }, ...hotbar.map(invCell)));
  panel.append(invRows);

  renderPalette();
  for (const i of [...hotbar, ...storage]) paintInv(i);

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey, true);
    // Cursor items are discarded in creative (infinite source).
    overlay.remove();
    game.uiOpen = false;
    void game.renderer.domElement.requestPointerLock();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (document.activeElement === search) return;
    if (e.code === 'Escape' || e.code === 'KeyE') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);
  overlay.addEventListener('mousemove', (e) => { cursorEl.style.left = `${e.clientX}px`; cursorEl.style.top = `${e.clientY}px`; });
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
}

function range(a: number, b: number): number[] {
  const out: number[] = [];
  for (let i = a; i < b; i++) out.push(i);
  return out;
}
